import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { MyContext } from '../../context';
import DateRangePicker from '../../components/DateRangePicker';
import LineChart from '../../components/charts/LineChart';
import DataTable from '../../components/tables/DataTable';
import { getOverviewReport } from '../../utils/queries';

const initialRange = () => {
	const now = dayjs();
	const yearStart = now.startOf('year');
	return {
		preset: 'YTD',
		from: yearStart.format('YYYY-MM-DD'),
		to: now.format('YYYY-MM-DD'),
		label: 'YTD (Year to Date)',
	};
};

const TOP_PRODUCTS_COLUMNS = [
	{ key: 'rank', header: '#', sortable: false },
	{ key: 'product_name', header: 'Product' },
	{ key: 'units_sold', header: 'Units Sold' },
];

function ChartCard({ title, value, children }) {
	return (
		<div className='border border-gray-200 rounded-lg p-5'>
			<p className='text-sm font-medium text-gray-700 mb-1'>{title}</p>
			<p className='text-2xl font-light mb-4'>{value}</p>
			{children}
		</div>
	);
}

export default function Home() {
	const { activeStoreId } = useContext(MyContext);
	const [range, setRange] = useState(initialRange);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!activeStoreId) return;
		setLoading(true);
		getOverviewReport({ storeId: activeStoreId, dateFrom: range.from, dateTo: range.to }).then((result) => {
			if (result?.success) setReport(result.data);
			setLoading(false);
		});
	}, [activeStoreId, range.from, range.to]);

	if (!activeStoreId) {
		return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	}

	const isCurrency = report?.payment_metric === 'currency';
	const unit = isCurrency ? '' : ' pts';
	const formatValue = (v) => (isCurrency ? `$${Number(v).toFixed(2)}` : Math.round(Number(v)));

	const dateLabels = (series) => series.map((d) => dayjs(d.date).format('MMM D'));

	const salesSeries = report?.sales_over_time || [];
	const ordersSeries = report?.orders_over_time || [];
	const avgSeries = report?.avg_order_value_over_time || [];
	const topProducts = report?.top_products || [];

	const salesData = {
		labels: dateLabels(salesSeries),
		datasets: [
			{
				label: 'Total Sales',
				data: salesSeries.map((d) => d.value),
				borderColor: '#0F8B8D',
				backgroundColor: 'rgba(15,139,141,0.08)',
				fill: true,
			},
		],
	};

	const ordersData = {
		labels: dateLabels(ordersSeries),
		datasets: [
			{
				label: 'Total Orders',
				data: ordersSeries.map((d) => d.value),
				borderColor: '#F28C43',
				backgroundColor: 'rgba(242,140,67,0.08)',
				fill: true,
			},
		],
	};

	const avgData = {
		labels: dateLabels(avgSeries),
		datasets: [
			{
				label: 'Avg Order Value',
				data: avgSeries.map((d) => d.value),
				borderColor: '#D6216B',
				backgroundColor: 'rgba(214,33,107,0.08)',
				fill: true,
			},
		],
	};

	const topProductsRows = topProducts.map((p, i) => ({ rank: i + 1, ...p }));

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Overview dashboard</h1>
				<DateRangePicker value={range} onChange={setRange} className='w-64' />
			</div>

			{loading || !report ? (
				<div className='text-sm text-gray-400'>Loading...</div>
			) : (
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
					<ChartCard title='Total Sales' value={`${formatValue(report.summary.total_sales)}${isCurrency ? '' : unit}`}>
						<LineChart data={salesData} height={220} unit={unit} formatValue={formatValue} />
					</ChartCard>

					<ChartCard title='Total Orders' value={report.summary.total_orders}>
						<LineChart data={ordersData} height={220} />
					</ChartCard>

					<ChartCard title='Avg Order Value' value={`${formatValue(report.summary.avg_order_value)}${isCurrency ? '' : unit}`}>
						<LineChart data={avgData} height={220} unit={unit} formatValue={formatValue} />
					</ChartCard>

					<ChartCard title='Top Products by Units Sold' value={topProducts.reduce((sum, p) => sum + p.units_sold, 0)}>
						<DataTable
							columns={TOP_PRODUCTS_COLUMNS}
							data={topProductsRows}
							emptyHeading='No units sold in this range'
						/>
					</ChartCard>
				</div>
			)}
		</div>
	);
}
